import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Alert,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
// Remove DatePicker imports to fix build issues
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { AuthLog, AuthEventType, AuthLogFilter } from '../../types/logging';
import { getAuthLogs, getSuspiciousActivityLogs, getAuthMetrics, resolveSuspiciousActivity } from '../../services/authLoggingService';
import { useAuth } from '../../contexts/AuthContext';

// Helper function to format timestamp
const formatDate = (timestamp: any) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString();
};

// Event type display names
const eventTypeLabels: Record<AuthEventType, string> = {
  'login_success': 'Login Success',
  'login_failure': 'Login Failure',
  'logout': 'Logout',
  'register': 'Registration',
  'email_verification': 'Email Verification',
  'password_reset_request': 'Password Reset Request',
  'password_reset_complete': 'Password Reset Complete',
  'profile_update': 'Profile Update',
  'role_change': 'Role Change',
  'admin_action': 'Admin Action'
};

// Status chip colors
const statusColors = {
  success: 'success',
  failure: 'error'
};

const AuthLogsMonitoring: React.FC = () => {
  const { currentUser, isAdmin } = useAuth();
  const [authLogs, setAuthLogs] = useState<AuthLog[]>([]);
  const [suspiciousActivities, setSuspiciousActivities] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedLog, setSelectedLog] = useState<AuthLog | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState<AuthLogFilter>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)), // Last 7 days
    endDate: new Date(),
    eventType: 'all',
    status: 'all',
    search: ''
  });

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch data based on current filters
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch auth logs
      const logs = await getAuthLogs(filters, 1000);
      setAuthLogs(logs);
      
      // Fetch suspicious activities (admin only)
      if (isAdmin()) {
        const activities = await getSuspiciousActivityLogs(false);
        setSuspiciousActivities(activities);
      }
      
      // Fetch metrics (admin only)
      if (isAdmin()) {
        const metricsData = await getAuthMetrics(30);
        setMetrics(metricsData);
      }
    } catch (error) {
      console.error('Error fetching auth data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    fetchData();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: new Date(),
      eventType: 'all',
      status: 'all',
      search: ''
    });
  };

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle log details view
  const handleViewLogDetails = (log: AuthLog) => {
    setSelectedLog(log);
    setOpenDialog(true);
  };

  // Handle suspicious activity resolution
  const handleResolveActivity = async (id: string) => {
    try {
      await resolveSuspiciousActivity(id);
      // Refresh suspicious activities
      const activities = await getSuspiciousActivityLogs(false);
      setSuspiciousActivities(activities);
    } catch (error) {
      console.error('Error resolving activity:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Authentication Logs & Monitoring
      </Typography>
      
      {/* Suspicious Activities Alert (Admin Only) */}
      {isAdmin() && suspiciousActivities.length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => fetchData()}>
              Refresh
            </Button>
          }
        >
          {suspiciousActivities.length} suspicious authentication activities detected! Review them below.
        </Alert>
      )}
      
      {/* Metrics Dashboard (Admin Only) */}
      {isAdmin() && metrics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">Total Auth Events</Typography>
                <Typography variant="h3">{metrics.totalEvents}</Typography>
                <Typography variant="body2" color="text.secondary">Last 30 days</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">Login Success Rate</Typography>
                <Typography variant="h3">
                  {metrics.successfulLogins && (metrics.successfulLogins + metrics.failedLogins) > 0
                    ? `${Math.round((metrics.successfulLogins / (metrics.successfulLogins + metrics.failedLogins)) * 100)}%`
                    : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {metrics.successfulLogins} successful / {metrics.failedLogins} failed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">New Registrations</Typography>
                <Typography variant="h3">{metrics.registrations}</Typography>
                <Typography variant="body2" color="text.secondary">Last 30 days</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">Admin Actions</Typography>
                <Typography variant="h3">{metrics.adminActions}</Typography>
                <Typography variant="body2" color="text.secondary">Last 30 days</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Email, details..."
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Event Type"
              value={filters.eventType || 'all'}
              onChange={(e) => handleFilterChange('eventType', e.target.value)}
            >
              <MenuItem value="all">All Events</MenuItem>
              {Object.entries(eventTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status || 'all'}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failure">Failure</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={applyFilters}>Apply</Button>
              <Button variant="outlined" onClick={resetFilters}>Reset</Button>
              <IconButton onClick={fetchData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Suspicious Activities Table (Admin Only) */}
      {isAdmin() && suspiciousActivities.length > 0 && (
        <Paper sx={{ width: '100%', mb: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>IP Addresses</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suspiciousActivities.map((activity) => (
                  <TableRow key={activity.id} sx={{ backgroundColor: '#fff8e1' }}>
                    <TableCell>{formatDate(activity.timestamp)}</TableCell>
                    <TableCell>{activity.email}</TableCell>
                    <TableCell>
                      <Chip 
                        icon={<WarningIcon />} 
                        label={activity.type.replace(/_/g, ' ')} 
                        color="warning"
                      />
                    </TableCell>
                    <TableCell>{activity.details}</TableCell>
                    <TableCell>{activity.ipAddresses?.join(', ') || 'N/A'}</TableCell>
                    <TableCell>
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => handleResolveActivity(activity.id)}
                      >
                        Resolve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      
      {/* Auth Logs Table */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Loading...</TableCell>
                </TableRow>
              ) : authLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No logs found</TableCell>
                </TableRow>
              ) : (
                authLogs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDate(log.timestamp)}</TableCell>
                      <TableCell>{log.email}</TableCell>
                      <TableCell>
                        {log.eventType && (
                          <Chip 
                            label={eventTypeLabels[log.eventType as AuthEventType] || log.eventType} 
                            size="small"
                            color={log.eventType.includes('failure') ? 'error' : 'primary'}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={log.status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                          label={log.status} 
                          color={log.status === 'success' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{log.ipAddress || 'N/A'}</TableCell>
                      <TableCell>
                        {log.details ? (
                          log.details.length > 30 ? `${log.details.substring(0, 30)}...` : log.details
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewLogDetails(log)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={authLogs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Log Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Authentication Log Details</DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Time</Typography>
                <Typography variant="body1" gutterBottom>{formatDate(selectedLog.timestamp)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Event Type</Typography>
                <Typography variant="body1" gutterBottom>
                  {eventTypeLabels[selectedLog.eventType as AuthEventType] || selectedLog.eventType}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Email</Typography>
                <Typography variant="body1" gutterBottom>{selectedLog.email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">User ID</Typography>
                <Typography variant="body1" gutterBottom>{selectedLog.userId || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Status</Typography>
                <Typography variant="body1" gutterBottom>{selectedLog.status}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">IP Address</Typography>
                <Typography variant="body1" gutterBottom>{selectedLog.ipAddress || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">User Agent</Typography>
                <Typography variant="body1" gutterBottom>{selectedLog.userAgent || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Location</Typography>
                <Typography variant="body1" gutterBottom>{selectedLog.location || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Details</Typography>
                <Typography variant="body1" gutterBottom>{selectedLog.details || 'N/A'}</Typography>
              </Grid>
              {selectedLog.error && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Error</Typography>
                  <Typography variant="body1" color="error" gutterBottom>{selectedLog.error}</Typography>
                </Grid>
              )}
              {selectedLog.adminAction && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Admin Action</Typography>
                  <Typography variant="body1" gutterBottom>
                    Yes (by {selectedLog.adminId || 'unknown'})
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuthLogsMonitoring;